<script setup>
    import CreateTask from "./createTask.vue";
    import {computed, onMounted, ref} from "vue";
    import IconChevron from "../icons/iconChevron.vue";
    import ButtonMoreOptionsOptionWithConfirmation from "../buttons/buttonMoreOptionsOptionWithConfirmation.vue";
    import ButtonMoreOptions from "../buttons/buttonMoreOptions.vue";

    const emit = defineEmits(['reload'])

    const props = defineProps({
        displaymode: {
            type: String,
            required: false,
            default: 'widget',
        },
        taskList: {
            type: Object,
            required: true,
        },
        expanded: {
            type: Boolean,
            required: false,
            default: false,
        }
    })

    const addingTask = ref(false)
    const _taskList = ref(JSON.parse(JSON.stringify(props.taskList)))
    const _expanded = ref(props.expanded)
    const _editingTaskListName = ref(false)

    const reloadTasklist = async () => {
        const res = await axios.get(route('api.task-list.show', props.taskList.id))
        _taskList.value = res.data.task_list
    }

    const onTaskCreated = () => {
        addingTask.value = false
        reloadTasklist()
    }

    const onTaskUpdated = () => {
        reloadTasklist()
    }

    const onTaskDeleted = () => {
        reloadTasklist()
    }

    const deleteTaskList = async () => {
        await axios.delete(route('api.task-list.delete', props.taskList.id))
        emit('reload')
    }

    const updateTaskListName = async () => {
        await axios.put(route('api.task-list.update', props.taskList.id), {
            name: _taskList.value.name
        })

        _editingTaskListName.value = false
    }

    const cancelEditingTaskListName = () => {
        _taskList.name = props.taskList.name
        _editingTaskListName.value = false
    }

    const taskHeaderClass = computed(() => {
        if (props.displaymode === 'standalone') {
            return 'bg-wlPrimary text-wlPrimaryContrast rounded-wl px-4 py-3'
        }
        return ''
    })

    const containerClass = computed(() => {
        const classes = [];
        if (props.displaymode === 'standalone') {
        } else if (props.displaymode === 'widget') {
            classes.push('-mx-4','md:-mx-6','px-4','md:px-6 border-b')
            if (_expanded.value) {
                classes.push('bg-gradient-to-b','from-white','to-gray-100')
            }
        }
        return classes.join(' ')
    })

    const orderedTasks = computed(() => {
        const completedTasks = _taskList.value.tasks.filter(task => task.is_completed);
        const activeTasks = _taskList.value.tasks.filter(task => !task.is_completed);
        return [...activeTasks, ...completedTasks];
    })

</script>

<template>
<div :class="containerClass">
    <div
        class="flex justify-between items-center space-x-2 cursor-pointer transition-all py-4 z-10 relative"
        :class="taskHeaderClass"
    >
        <div
            v-if="! _editingTaskListName"
            class="relative"
        >
            <h3 @click="_expanded = ! _expanded">{{ _taskList.name }}</h3>
            <span class="absolute left-full top-0 bottom-0 flex items-center ml-2">
                <button-transparent
                    v-if="_expanded"
                    size="line"
                    @click="_editingTaskListName = true"
                >
                    <i class="fa-regular fa-pen text-gray-400"></i>
                </button-transparent>
            </span>
        </div>
        <div
            v-if="_editingTaskListName"
            class="flex w-full space-x-2 items-center"
        >
            <div class="w-full -translate-y-0.5">
                <input-text
                    v-model:content="_taskList.name"
                    class="!mb-0"
                    autofocus
                />
            </div>
            <button-primary
                size="sm"
                @click="updateTaskListName"
            >{{ _mft('shared:action.save') }}</button-primary>
            <button-secondary
                size="sm"
                @click="cancelEditingTaskListName"
            >{{ _mft('shared:action.cancel') }}</button-secondary>
        </div>
        <span
            class="transition-all"
            :class="_expanded ? 'rotate-180' : ''"
            @click="_expanded = ! _expanded"
        >
            <IconChevron class="w-5 h-5 fill-current" />
        </span>
    </div>
    <div
        v-if="_expanded"
        class="mb-4"
        :class="displaymode === 'standalone' ? 'px-4 bg-white translate-y-px rounded-wl py-4 -mt-4' : ''"
    >
        <ul class="divide-y mt-2">
            <li
                v-for="(task, t) in orderedTasks"
                class="-mx-4 lg:-mx-6 px-4 lg:px-6"
            >
                <task
                    :task="task"
                    @deleted="onTaskDeleted"
                    @updated="onTaskUpdated"
                    @completed="quickReorderList"
                ></task>
            </li>
        </ul>
        <create-task
            v-if="addingTask"
            :task-list="_taskList"
            class="py-3 -mx-4 lg:-mx-6 px-4 lg:px-6 bg-gray-100 border-t"
            @created="onTaskCreated"
            @cancel="addingTask = false"
        ></create-task>
        <div
            v-if="!addingTask"
            class="flex justify-between mt-4"
        >
            <button-more-options
                alignment="right"
                position="side"
                size="sm"
                :cancel-on-outside-click="false"
            >
                <button-more-options-option-with-confirmation
                    style-type="danger"
                    confirm-text="Are you sure you want to delete this task list?"
                    confirm-button-label="Delete"
                    abort-button-label="Cancel"
                    @confirmed="deleteTaskList"
                    class="w-36"
                >
                    Delete task list
                </button-more-options-option-with-confirmation>
            </button-more-options>

            <button-primary
                v-if="_expanded && ! addingTask"
                size="sm"
                @click="addingTask = ! addingTask"
            >
                {{ addingTask ? 'Cancel' : 'Add task' }}
            </button-primary>
        </div>
    </div>
</div>
</template>
